/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from "react";
import LogNewMediaContainerForm from "../../../../common/commonComponents/logNewMediaContainerForm";
import { ContainerInventoryService } from "../../../../../service/MediaContainerServices";
import { useTranslation } from 'react-i18next';



interface ContainerLotCreationProps {
  onSave: () => void;
  onCancel: () => void;
  showToastMessage: (message: string, type: string) => void;
}

const ContainerLotCreation: React.FC<ContainerLotCreationProps> = ({
  onSave,
  onCancel,
  showToastMessage,
}) => {
    const { t } = useTranslation();
    const [containerTypes, setContainerTypes] = useState<any[]>([]);
    const [capacityList, setCapacityList] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    const [formData, setFormData] = useState({
    containerTypeId: "",
    capacityId: "",
    manufacturerLotNumber: "",
    arrivalDate: "",
    totalContainers: "",
    noOfLots: "",
    });


    interface Errors {
    containerTypeId?: string;
    capacityId?: string;
    manufacturerLotNumber?: string;
    totalContainers?: string;
    noOfLots?: string;
    arrivalDate?: string; 
    }

    const [errors, setErrors] = useState<Errors>({});
    const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
        ...prev,
        [field]: value,
    }));
    };


    const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData.containerTypeId) newErrors.containerTypeId = t('containerInventory.errors.containerType');
    if (!formData.capacityId) newErrors.capacityId = t('containerInventory.errors.dimension');
    if (!formData.manufacturerLotNumber) newErrors.manufacturerLotNumber = t('containerInventory.errors.manufacturer');
    if (!formData.totalContainers) {
        newErrors.totalContainers = t('containerInventory.errors.unit');
    } else if (Number(formData.totalContainers) <= 0) {
        newErrors.totalContainers = t('containerInventory.errors.quantity');
    }
    if (!formData.noOfLots) {
        newErrors.noOfLots = t('containerInventory.errors.lots');
    } else if (
        Number(formData.noOfLots) <= 0 ||
        Number(formData.noOfLots) > Number(formData.totalContainers)) 
    {
        newErrors.noOfLots =t('containerInventory.errors.lotValidation');
    }
    if (!formData.arrivalDate) newErrors.arrivalDate = t('containerInventory.errors.date');
    if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return false;
    }
    setErrors({});
        return true;
    };



    //get container type list for dropdown
    const getContainerTypes = async () => {
    try {
        const response =
        await ContainerInventoryService.getContainerTypes();

        if (response?.status && response.statusCode === 200) {
        setContainerTypes(response.data);
        }
    } catch (error) {
        console.error("Container types error:", error);
    }
    };


    //get container capacities list for dropdown
    const getCapacities = async (containerTypeId: string) => {
    try {
        const response =
        await ContainerInventoryService.getContainerCapacities(
            containerTypeId
        );

        if (response?.status && response.statusCode === 200) {
        setCapacityList(response.data);
        } else {
        setCapacityList([]);
        }
    } catch (error) {
        console.error("Capacity error:", error);
    }
    };


    useEffect(() => {
    getContainerTypes();
    }, []);


    useEffect(() => {
    if (formData.containerTypeId) {
        getCapacities(formData.containerTypeId);
    } else {
        setCapacityList([]);
    }
    }, [formData.containerTypeId]);


    //save
    const handleSave = async () => {
        if (isSaving) return;
        if (!validateForm()) return;

        const payload = {
            container_type_id: Number(formData.containerTypeId),
            container_size_id: Number(formData.capacityId),
            batch_no: formData.manufacturerLotNumber,
            quantity: Number(formData.totalContainers),
            lot_size: Number(formData.noOfLots),
            log_arrival_date: formData.arrivalDate,
            source_type: "In-House",
        };

        try {
            setIsSaving(true);

            const response =
            await ContainerInventoryService.createContainerInventory(payload);

            if (response?.status) {
            showToastMessage(response?.message, "success");
            onSave();
            return;
            } else {
            showToastMessage(response?.message, "error");
            }
        } catch (error) {
            showToastMessage("Error saving container inventory", "error");
        } finally {
            setIsSaving(false);
        }
    };


  return (
    <LogNewMediaContainerForm
    formData={formData}
    containerTypes={containerTypes}
    capacityList={capacityList}
    errors={errors}
    onChange={handleChange}
    onSave={handleSave}
    isSaving={isSaving}
    onCancel={onCancel}
    />
  );
};

export default ContainerLotCreation;